// src/features/auth/LoginPage.tsx
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Activity, Eye, EyeOff, ArrowRight } from "lucide-react";
import { authService } from "../../api/services";
import { useAuthStore } from "./auth.store";
import { AuthUser } from "../../api/types";
import toast from "react-hot-toast";

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loginError, setLoginError] = useState("");

  const handleStaffLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    try {
      const res = await authService.staffLogin({ username, password });
      const user: AuthUser = {
        id: 0,
        name: res.name,
        role: res.role as AuthUser["role"],
        token: res.token,
        expiresAt: res.expiresAt,
        username,
        refreshToken: res.refreshToken,
      };
      login(user);
      toast.success(`Welcome back, ${res.name}!`);
      navigate("/");
    } catch (err: any) {
      setLoginError(err.message || "Invalid username or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* ── Left panel (branding) ──────────────────────────────────────── */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-brand-700 p-12 relative overflow-hidden">
        {/* Background texture */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                              radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-lg leading-none">
              RMV Hospital
            </div>
            <div className="text-brand-200 text-xs mt-0.5">
              Appointment Management
            </div>
          </div>
        </div>

        {/* Center copy */}
        <div className="relative">
          <h1 className="font-display text-5xl font-bold text-white leading-tight mb-4">
            Healthcare,
            <br />
            <span className="text-teal-400">Simplified.</span>
          </h1>
          <p className="text-brand-200 text-base leading-relaxed max-w-sm">
            Book appointments, manage your health records, and connect with
            specialist doctors — all in one seamless platform.
          </p>

          {/* Stats */}
          <div className="flex gap-8 mt-10">
            {[
              { label: "Doctors", value: "50+" },
              { label: "Specialties", value: "20+" },
              { label: "Patients", value: "10k+" },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl font-bold text-white">
                  {stat.value}
                </div>
                <div className="text-xs text-brand-300 mt-0.5">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-xs text-brand-300">
          &copy; {new Date().getFullYear()} RMV Hospital, Bangalore. All rights
          reserved.
        </div>
      </div>

      {/* ── Right panel (form) ─────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <Activity className="w-6 h-6 text-brand-600" />
          <span className="font-bold text-slate-900">RMV Hospital</span>
        </div>

        <div className="w-full max-w-md">
          <div className="animate-slide-in">
            <h2 className="text-2xl font-semibold text-slate-900 mb-1">
              Staff Sign in
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              Use your staff credentials to access the system.
            </p>

            <form onSubmit={handleStaffLogin} className="space-y-4">
              <div>
                <label className="label">Username</label>
                <input
                  className="input"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setLoginError(""); }}
                  placeholder="e.g. admin"
                  autoComplete="username"
                  required
                />
              </div>

              <div>
                <label className="label">Password</label>
                <div className="relative">
                  <input
                    className="input pr-10"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setLoginError(""); }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPw ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {loginError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <p className="font-medium">{loginError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full btn-lg justify-center"
              >
                {loading ? (
                  "Signing in…"
                ) : (
                  <>
                    Sign in <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

          </div>

          {/* Patient login link */}
          <div className="mt-8 text-center">
            <p className="text-xs text-slate-400">
              Are you a patient?{" "}
              <Link to="/patient-login" className="text-brand-600 hover:text-brand-700 font-medium">
                Login with OTP
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
